/// If the selection is empty and the cursor is at the end of a
/// textblock, try to reduce or remove the boundary between that block
/// and the one after it, either by joining them or by moving the other
/// block closer to this one in the tree structure. Will use the view
import { Fragment, type Node, NodeRange, type ResolvedPos, Slice } from "prosemirror-model";
import {
	type Command,
	type EditorState,
	NodeSelection,
	Selection,
	type TextSelection,
	type Transaction,
} from "prosemirror-state";
import {
	ReplaceAroundStep,
	type ReplaceStep,
	canJoin,
	liftTarget,
	replaceStep,
} from "prosemirror-transform";
import { type EditorView } from "prosemirror-view";

function atBlockEnd(state: EditorState, view?: EditorView): ResolvedPos | null {
	let { $cursor } = state.selection as TextSelection;
	if (
		!$cursor ||
		(view
			? !view.endOfTextblock("forward", state)
			: $cursor.parentOffset < $cursor.parent.content.size)
	)
		return null;
	return $cursor;
}

function findCutAfter($pos: ResolvedPos) {
	if (!$pos.parent.type.spec.isolating)
		for (let i = $pos.depth - 1; i >= 0; i--) {
			let parent = $pos.node(i);
			if ($pos.index(i) + 1 < parent.childCount) return $pos.doc.resolve($pos.after(i + 1));
			if (parent.type.spec.isolating) break;
		}
	return null;
}

function joinMaybeClear(
	state: EditorState,
	$pos: ResolvedPos,
	dispatch: ((tr: Transaction) => void) | undefined
) {
	let before = $pos.nodeBefore,
		after = $pos.nodeAfter,
		index = $pos.index();
	if (!before || !after || !before.type.compatibleContent(after.type)) return false;
	if (!before.content.size && $pos.parent.canReplace(index - 1, index)) {
		if (dispatch)
			dispatch(state.tr.delete($pos.pos - before.nodeSize, $pos.pos).scrollIntoView());
		return true;
	}
	if (
		!$pos.parent.canReplace(index, index + 1) ||
		!(after.isTextblock || canJoin(state.doc, $pos.pos))
	)
		return false;
	if (dispatch) dispatch(state.tr.join($pos.pos).scrollIntoView());
	return true;
}

/**
 * Delete's the "barrier" between nodes.
 *
 * When we press shift+delete (on macOS) at the end of a block, what should happen? Sometimes we
 * join compatible blocks together (`joinMaybeClear`). Sometimes we try to wrap the next block in
 * the current block's structure. Sometimes we lift the next block (`liftTarget`). Sometimes we
 * delete the current block and select the next block (`deleteBarrier`).
 *
 * This function handles all those things.
 */
function deleteBarrier(
	state: EditorState,
	$cut: ResolvedPos,
	dispatch: ((tr: Transaction) => void) | undefined,
	dir: number
) {
	let before = $cut.nodeBefore!,
		after = $cut.nodeAfter!,
		conn,
		match;
	let isolated = before.type.spec.isolating || after.type.spec.isolating;
	if (!isolated && joinMaybeClear(state, $cut, dispatch)) return true;

	let canDelAfter = !isolated && $cut.parent.canReplace($cut.index(), $cut.index() + 1);
	if (
		canDelAfter &&
		(conn = (match = before.contentMatchAt(before.childCount)).findWrapping(after.type)) &&
		match.matchType(conn[0] || after.type)!.validEnd
	) {
		if (dispatch) {
			let end = $cut.pos + after.nodeSize,
				wrap = Fragment.empty;
			for (let i = conn.length - 1; i >= 0; i--)
				wrap = Fragment.from(conn[i].create(null, wrap));
			wrap = Fragment.from(before.copy(wrap));
			let tr = state.tr.step(
				new ReplaceAroundStep(
					$cut.pos - 1,
					end,
					$cut.pos,
					end,
					new Slice(wrap, 1, 0),
					conn.length,
					true
				)
			);
			let $joinAt = tr.doc.resolve(end + 2 * conn.length);
			if (
				$joinAt.nodeAfter &&
				$joinAt.nodeAfter.type == before.type &&
				canJoin(tr.doc, $joinAt.pos)
			)
				tr.join($joinAt.pos);
			dispatch(tr.scrollIntoView());
		}
		return true;
	}

	// If the user is in an empty paragraph and the next block is a list item, and they press
	// shift+delete (on macOS), we want to delete the current paragraph, and we do NOT want to
	// convert the list item into a paragraph (aka "delete the 'barrier'"). We return `false` so
	// that `joinForward` does exactly this.
	if (!before.content.size) {
		return false;
	}

	let selAfter =
		after.type.spec.isolating || (dir > 0 && isolated) ? null : Selection.findFrom($cut, 1);
	let range = selAfter && selAfter.$from.blockRange(selAfter.$to);
	if (!range.parent.type.inlineContent && !range.parent.type.isLeaf && range.parent.childCount) {
		range = new NodeRange(
			$cut.doc.resolve(range.start + 1),
			$cut.doc.resolve(range.end - 1),
			range.depth + 1
		);
	}
	let target = range && liftTarget(range);
	if (target != null && target >= $cut.depth) {
		if (dispatch) dispatch(state.tr.lift(range!, target).scrollIntoView());
		return true;
	}

	if (canDelAfter && textblockAt(after, "start", true) && textblockAt(before, "end")) {
		let at = before,
			wrap = [];
		for (;;) {
			wrap.push(at);
			if (at.isTextblock) break;
			at = at.lastChild!;
		}
		let afterText = after,
			afterDepth = 1;
		for (; !afterText.isTextblock; afterText = afterText.firstChild!) afterDepth++;
		if (at.canReplace(at.childCount, at.childCount, afterText.content)) {
			if (dispatch) {
				let end = Fragment.empty;
				for (let i = wrap.length - 1; i >= 0; i--) end = Fragment.from(wrap[i].copy(end));
				let tr = state.tr.step(
					new ReplaceAroundStep(
						$cut.pos - wrap.length,
						$cut.pos + after.nodeSize,
						$cut.pos + afterDepth,
						$cut.pos + after.nodeSize - afterDepth,
						new Slice(end, wrap.length, 0),
						0,
						true
					)
				);
				dispatch(tr.scrollIntoView());
			}
			return true;
		}
	}

	return false;
}

function textblockAt(node: Node, side: "start" | "end", only = false) {
	for (
		let scan: Node | null = node;
		scan;
		scan = side == "start" ? scan.firstChild : scan.lastChild
	) {
		if (scan.isTextblock) return true;
		if (only && scan.childCount != 1) return false;
	}
	return false;
}

/**
 * Fork of joinForward that lifts nested blocks before joining.
 *
 * Example:
 *
 * ```
 * paragraph|
 * - list item
 * ```
 *
 * Becomes
 *
 * ```
 * paragraph|list item
 * ```
 *
 * Instead of
 *
 * ```
 * paragraph|
 * list item
 * ```
 */
/// for accurate start-of-textblock detection if given.
export const joinForward: Command = (state, dispatch, view) => {
	let $cursor = atBlockEnd(state, view);
	if (!$cursor) return false;

	let $cut = findCutAfter($cursor);
	// If there is no node after this, there's nothing to do
	if (!$cut) return false;

	let after = $cut.nodeAfter!;
	// Try the joining algorithm
	if (deleteBarrier(state, $cut, dispatch, 1)) return true;

	// If the node above has no content and the node below is
	// selectable, delete the node above and select the one below.
	if (
		$cursor.parent.content.size == 0 &&
		(textblockAt(after, "start") || NodeSelection.isSelectable(after))
	) {
		let delStep = replaceStep(state.doc, $cursor.before(), $cursor.after(), Slice.empty);
		if (
			delStep &&
			(delStep as ReplaceStep).slice.size <
				(delStep as ReplaceStep).to - (delStep as ReplaceStep).from
		) {
			if (dispatch) {
				let tr = state.tr.step(delStep);
				tr.setSelection(
					textblockAt(after, "start")
						? Selection.findFrom(tr.doc.resolve(tr.mapping.map($cut.pos)), 1)!
						: NodeSelection.create(tr.doc, tr.mapping.map($cut.pos))
				);
				dispatch(tr.scrollIntoView());
			}
			return true;
		}
	}

	// If the next node is an atom, delete it
	if (after.isAtom && $cut.depth == $cursor.depth - 1) {
		if (dispatch)
			dispatch(state.tr.delete($cut.pos, $cut.pos + after.nodeSize).scrollIntoView());
		return true;
	}

	return false;
};
